/**
 * Enums - Game Enumerations
 *
 * This file contains all enums used throughout the game.
 * Enums provide type-safe constants with meaningful names.
 *
 * TypeScript Concepts Demonstrated:
 * - Numeric enums (default behavior)
 * - String enums (explicit string values)
 * - Const enums (inlined at compile time)
 * - Using enums as types
 *
 * @example
 * let state: GameState = GameState.Playing;
 * if (state === GameState.Paused) {
 *   console.log('Game is paused');
 * }
 */

// ============================================
// GAME STATE
// ============================================

/**
 * Main game states
 * The game can only be in one of these states at a time
 */
export enum GameState {
  Loading = 'LOADING',
  MainMenu = 'MAIN_MENU',
  Playing = 'PLAYING',
  Paused = 'PAUSED',
  Inventory = 'INVENTORY',
  Crafting = 'CRAFTING',
  Building = 'BUILDING',
  Dialogue = 'DIALOGUE',
  GameOver = 'GAME_OVER'
}

/**
 * Player character states
 */
export enum PlayerState {
  Idle = 'IDLE',
  Walking = 'WALKING',
  Running = 'RUNNING',
  Gathering = 'GATHERING',
  Attacking = 'ATTACKING',
  Crafting = 'CRAFTING',
  Building = 'BUILDING',
  Dead = 'DEAD'
}

/**
 * AI behavior states for enemies and NPCs
 */
export enum AIState {
  Idle = 'IDLE',
  Patrol = 'PATROL',
  Chase = 'CHASE',
  Attack = 'ATTACK',
  Flee = 'FLEE',
  Return = 'RETURN'
}

// ============================================
// ITEMS & RESOURCES
// ============================================

/**
 * Categories of items
 */
export enum ItemType {
  Resource = 'RESOURCE',
  Tool = 'TOOL',
  Weapon = 'WEAPON',
  Armor = 'ARMOR',
  Consumable = 'CONSUMABLE',
  Material = 'MATERIAL',
  Blueprint = 'BLUEPRINT',
  Quest = 'QUEST'
}

/**
 * Types of gatherable resources
 */
export enum ResourceType {
  Wood = 'WOOD',
  Stone = 'STONE',
  Iron = 'IRON',
  Coal = 'COAL',
  Food = 'FOOD',
  Fiber = 'FIBER',
  Water = 'WATER'
}

/**
 * Types of tools
 */
export enum ToolType {
  Axe = 'AXE',
  Pickaxe = 'PICKAXE',
  Shovel = 'SHOVEL',
  Hammer = 'HAMMER',
  Sickle = 'SICKLE',
  Drill = 'DRILL'
}

/**
 * Types of weapons
 */
export enum WeaponType {
  Sword = 'SWORD',
  Spear = 'SPEAR',
  Bow = 'BOW',
  Club = 'CLUB'
}

/**
 * Item rarity levels
 */
export enum Rarity {
  Common = 'COMMON',
  Uncommon = 'UNCOMMON',
  Rare = 'RARE',
  Epic = 'EPIC',
  Legendary = 'LEGENDARY'
}

// ============================================
// WORLD & STRUCTURES
// ============================================

/**
 * Types of terrain tiles
 */
export enum TileType {
  Grass = 'GRASS',
  Dirt = 'DIRT',
  Stone = 'STONE',
  Water = 'WATER',
  Sand = 'SAND',
  Snow = 'SNOW',
  Tree = 'TREE',
  Rock = 'ROCK',
  Bush = 'BUSH',
  IronOre = 'IRON_ORE'
}

/**
 * Types of buildable structures
 */
export enum StructureType {
  Wall = 'WALL',
  Floor = 'FLOOR',
  Door = 'DOOR',
  Chest = 'CHEST',
  Storage = 'STORAGE',
  CraftingStation = 'CRAFTING_STATION',
  Furnace = 'FURNACE',
  Bed = 'BED',
  Light = 'LIGHT',
  HellHole = 'HELL_HOLE',
  Ladder = 'LADDER'
}

/**
 * Types of crafting stations
 */
export enum CraftingStationType {
  None = 'NONE',          // Can craft by hand
  Workbench = 'WORKBENCH',
  Furnace = 'FURNACE',
  Anvil = 'ANVIL',
  CookingPot = 'COOKING_POT',
  AlchemyTable = 'ALCHEMY_TABLE'
}

// ============================================
// DIRECTIONS & LAYERS
// ============================================

/**
 * Cardinal directions
 * Used for player facing and movement
 */
export enum Direction {
  North = 'NORTH',    // Up in isometric
  South = 'SOUTH',    // Down in isometric
  East = 'EAST',      // Right in isometric
  West = 'WEST',      // Left in isometric
  NorthEast = 'NORTH_EAST',
  NorthWest = 'NORTH_WEST',
  SouthEast = 'SOUTH_EAST',
  SouthWest = 'SOUTH_WEST'
}

/**
 * Render layers for depth sorting
 * Lower numbers render first (behind)
 */
export enum LayerType {
  Ground = 0,
  GroundDecor = 1,
  Shadows = 2,
  Objects = 3,
  Entities = 4,
  Effects = 5,
  UI = 6
}

// ============================================
// COMPONENTS
// ============================================

/**
 * Types of entity components
 * Used for the component system
 */
export enum ComponentType {
  Health = 'HEALTH',
  Inventory = 'INVENTORY',
  Combat = 'COMBAT',
  Movement = 'MOVEMENT',
  Stamina = 'STAMINA',
  AI = 'AI'
}

/**
 * Types of entities
 */
export enum EntityType {
  Player = 'PLAYER',
  Enemy = 'ENEMY',
  NPC = 'NPC',
  DroppedItem = 'DROPPED_ITEM',
  Projectile = 'PROJECTILE'
}

// ============================================
// COMBAT
// ============================================

/**
 * Types of damage
 */
export enum DamageType {
  Physical = 'PHYSICAL',
  Fire = 'FIRE',
  Cold = 'COLD',
  Poison = 'POISON'
}

/**
 * Equipment slots
 */
export enum EquipmentSlot {
  Head = 'HEAD',
  Body = 'BODY',
  Legs = 'LEGS',
  Feet = 'FEET',
  MainHand = 'MAIN_HAND',
  OffHand = 'OFF_HAND'
}

// ============================================
// INPUT
// ============================================

/**
 * Types of input events
 */
export enum InputEventType {
  KeyDown = 'KEY_DOWN',
  KeyUp = 'KEY_UP',
  KeyPressed = 'KEY_PRESSED',
  MouseMove = 'MOUSE_MOVE',
  MouseDown = 'MOUSE_DOWN',
  MouseUp = 'MOUSE_UP',
  Click = 'CLICK',
  RightClick = 'RIGHT_CLICK',
  Scroll = 'SCROLL'
}

/**
 * Key states for tracking held keys
 */
export enum KeyState {
  Up = 'UP',
  Down = 'DOWN',
  JustPressed = 'JUST_PRESSED',
  JustReleased = 'JUST_RELEASED'
}
